(() => {
  const money = (cents, format = '${{amount}}') => {
    const value = (Number(cents || 0) / 100).toFixed(2);
    return format.replace('{{amount}}', value).replace('{{amount_no_decimals}}', String(Math.round(Number(cents || 0) / 100)));
  };

  class VeloraHeader extends HTMLElement {
    connectedCallback() {
      const toggle = this.querySelector('[data-menu-toggle]');
      const nav = this.querySelector('[data-mobile-menu]');
      toggle?.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        nav?.classList.toggle('is-open', open);
      });
      nav?.addEventListener('click', (event) => {
        if (!event.target.closest('a')) return;
        toggle?.setAttribute('aria-expanded', 'false');
        nav.classList.remove('is-open');
      });
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 18);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  class VeloraProduct extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.idInput = this.form?.querySelector('[name="id"]');
      this.prices = [...this.querySelectorAll('[data-product-price]')];
      this.compare = this.querySelector('[data-product-compare-price]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.stickyButton = this.querySelector('[data-sticky-add]');
      this.selectors = [...this.querySelectorAll('[data-option-position]')];
      this.selectors.forEach((group) => group.addEventListener('change', () => this.syncVariant()));
      this.querySelectorAll('[data-thumbnail]').forEach((button) => button.addEventListener('click', () => this.showMedia(button.dataset.mediaId)));
      this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.moveMedia(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.moveMedia(1));
      this.stickyButton?.addEventListener('click', () => this.form?.requestSubmit());
      this.setupSwipe();
      this.form?.addEventListener('submit', () => { if (this.addButton) this.addButton.textContent = 'ADDING…'; });
    }

    selectedOptions() {
      return this.selectors.map((group) => group.querySelector('input:checked, select')?.value).filter(Boolean);
    }

    syncVariant() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
      if (!variant) {
        if (this.addButton) { this.addButton.disabled = true; this.addButton.textContent = 'UNAVAILABLE'; }
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      this.prices.forEach((node) => { node.textContent = money(variant.price, this.dataset.moneyFormat); });
      if (this.compare) {
        this.compare.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price, this.dataset.moneyFormat) : '';
        this.compare.hidden = !(variant.compare_at_price > variant.price);
      }
      [this.addButton, this.stickyButton].forEach((button) => {
        if (!button) return;
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
      });
      const stock = this.querySelector('[data-stock-line]');
      stock?.classList.toggle('is-sold-out', !variant.available);
      const stockText = stock?.querySelector('span');
      if (stockText) stockText.textContent = variant.available ? 'In stock — ready to ship' : 'Currently unavailable';
      if (variant.featured_media_id) this.showMedia(String(variant.featured_media_id));
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    showMedia(id) {
      if (!id) return;
      this.querySelectorAll('[data-product-media]').forEach((media) => media.classList.toggle('is-active', media.dataset.mediaId === id));
      this.querySelectorAll('[data-thumbnail]').forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId === id));
      const media = [...this.querySelectorAll('[data-product-media]')];
      const activeIndex = media.findIndex((item) => item.dataset.mediaId === id);
      const counter = this.querySelector('[data-gallery-counter]');
      if (counter && activeIndex >= 0) counter.textContent = `${activeIndex + 1} / ${media.length}`;
    }

    moveMedia(direction) {
      const media = [...this.querySelectorAll('[data-product-media]')];
      if (!media.length) return;
      const current = media.findIndex((item) => item.classList.contains('is-active'));
      const next = (current + direction + media.length) % media.length;
      this.showMedia(media[next]?.dataset.mediaId);
    }

    setupSwipe() {
      const gallery = this.querySelector('[data-product-gallery]');
      let start = null;
      gallery?.addEventListener('touchstart', (event) => { start = event.touches[0].clientX; }, { passive: true });
      gallery?.addEventListener('touchend', (event) => {
        if (start === null) return;
        const delta = event.changedTouches[0].clientX - start;
        if (Math.abs(delta) > 45) this.moveMedia(delta > 0 ? -1 : 1);
        start = null;
      }, { passive: true });
    }
  }

  const reveal = () => {
    const items = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) { items.forEach((item) => item.classList.add('is-visible')); return; }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }), { threshold: 0.12, rootMargin: '0px 0px -40px' });
    items.forEach((item) => observer.observe(item));
  };

  const setupLightbox = () => {
    const dialog = document.querySelector('[data-image-lightbox]');
    const target = dialog?.querySelector('[data-lightbox-image]');
    const close = () => { if (dialog) dialog.hidden = true; document.body.style.overflow = ''; };
    document.addEventListener('click', (event) => {
      const image = event.target.closest('img');
      if (!image || !dialog || !target || image.closest('[data-image-lightbox]')) return;
      target.src = image.currentSrc || image.src;
      target.alt = image.alt || 'Enlarged image';
      dialog.hidden = false;
      document.body.style.overflow = 'hidden';
    });
    dialog?.querySelector('[data-lightbox-close]')?.addEventListener('click', close);
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  };

  customElements.define('velora-header', VeloraHeader);
  customElements.define('velora-product', VeloraProduct);
  document.addEventListener('DOMContentLoaded', () => { reveal(); setupLightbox(); });
  document.addEventListener('shopify:section:load', reveal);
})();
